'use client';

import { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ImagePlus, Pencil, Trash2, X, Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useCategoriasServicio } from '@/components/hooks/useCategoriasServicio';
import {
  DLG_BTN_PRIMARY,
  DLG_BTN_SECONDARY,
} from '@/components/ui/dialogButtons';

export type CategoriaServicio = {
  id_categoria_servicio: string;
  nombre: string;
  imagen?: string | null;
  icono?: string | null;
  orden?: number | null;
  activo?: boolean;
};

type Props = {
  open: boolean;
  onClose: () => void;
  empresaId: string;
  mode: 'create' | 'manage' | 'edit';
  initialCategory?: CategoriaServicio;
  onSaved: (nueva?: CategoriaServicio) => void;
};

const SHEET_CLASS = `
  z-[60] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
  [&>button:last-child]:hidden
  flex flex-col
  !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
  w-full max-w-none max-h-[85vh] rounded-t-2xl rounded-b-none
  data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
  sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
  sm:w-[95%] sm:max-w-lg sm:mx-auto sm:h-auto sm:max-h-[85vh] sm:rounded-xl sm:shadow-xl
`;

// ============================================================
// CategoryForm
// ============================================================
export function CategoryForm({
  initial,
  isNew,
  onSaved,
  onCancel,
  createCategoria,
  updateCategoria,
  busyExternal,
}: {
  initial?: CategoriaServicio;
  isNew: boolean;
  onSaved: (cat: CategoriaServicio) => void;
  onCancel: () => void;
  createCategoria: (nombre: string, file?: File) => Promise<any>;
  updateCategoria: (
    id: string,
    nombre: string,
    file?: File | null
  ) => Promise<any>;
  busyExternal: boolean;
}) {
  const [nombre, setNombre] = useState(initial?.nombre ?? '');
  const [imagenPreview, setImagenPreview] = useState<string | null>(
    (initial?.imagen as string | null) ?? null
  );
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const imagenFileRef = useRef<File | null>(null);

  useEffect(() => {
    setNombre(initial?.nombre ?? '');

    if (initial?.imagen) {
      const base = (initial.imagen as string).split('?')[0];
      setImagenPreview(`${base}?t=${Date.now()}`);
    } else {
      setImagenPreview(null);
    }

    imagenFileRef.current = null;
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [initial?.id_categoria_servicio, initial?.imagen, initial?.nombre]);

  const isBusy = saving || busyExternal;

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    imagenFileRef.current = file;
    setImagenPreview(URL.createObjectURL(file));
  };

  const removeImage = () => {
    imagenFileRef.current = null;
    setImagenPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSave = async () => {
    if (!nombre.trim()) {
      toast.error('El nombre es obligatorio.');
      return;
    }

    setSaving(true);

    const fileToUpload = imagenFileRef.current;

    try {
      if (isNew) {
        const resp = await createCategoria(
          nombre.trim(),
          fileToUpload ?? undefined
        );

        if (!resp?.ok || !resp?.data) {
          toast.error('Error al crear la categoría.');
          return;
        }

        const created = resp.data as any;

        toast.success('Categoría creada.');
        onSaved({
          id_categoria_servicio: created.id_categoria_servicio,
          nombre: created.nombre,
          imagen: created.imagen ?? null,
          icono: created.icono ?? null,
          orden: created.orden ?? null,
          activo: created.activo ?? true,
        });
        return;
      }

      if (!initial?.id_categoria_servicio) {
        toast.error('Categoría inválida.');
        return;
      }

      const removedExistingImage =
        !imagenPreview && !fileToUpload && !!initial.imagen;

      const resp = await updateCategoria(
        initial.id_categoria_servicio,
        nombre.trim(),
        fileToUpload ? fileToUpload : removedExistingImage ? null : undefined
      );

      if (!resp?.ok || !resp?.data) {
        toast.error('Error al guardar la categoría.');
        return;
      }

      const updated = resp.data as any;

      toast.success('Categoría actualizada.');
      onSaved({
        id_categoria_servicio: updated.id_categoria_servicio,
        nombre: updated.nombre,
        imagen: updated.imagen ?? null,
        icono: updated.icono ?? null,
        orden: updated.orden ?? null,
        activo: updated.activo ?? true,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">
          Nombre
        </label>
        <Input
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          placeholder="Ej: Corte, Coloración…"
          className="dark:bg-white/5 dark:border-white/10 dark:text-white"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !isBusy) handleSave();
          }}
          autoFocus
        />
      </div>

      <div>
        <label className="block text-xs font-medium mb-2 text-gray-700 dark:text-gray-300">
          Imagen
        </label>

        {imagenPreview ? (
          <div className="relative w-full h-32 rounded-lg overflow-hidden border border-gray-200 dark:border-white/10 group">
            <img
              src={imagenPreview}
              alt="Vista previa"
              className="w-full h-full object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />

            {busyExternal && (
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                <Loader2 className="w-5 h-5 text-white animate-spin" />
              </div>
            )}

            {!busyExternal && (
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="p-1.5 rounded-full bg-white/90 text-gray-700 hover:bg-white transition-colors"
                  title="Cambiar"
                >
                  <Upload className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={removeImage}
                  className="p-1.5 rounded-full bg-white/90 text-red-600 hover:bg-white transition-colors"
                  title="Eliminar"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={busyExternal}
            className="w-full h-24 rounded-lg border-2 border-dashed border-gray-300 dark:border-white/10 flex flex-col items-center justify-center gap-1.5 text-gray-400 dark:text-gray-500 hover:border-[#103254]/50 dark:hover:border-blue-500/50 hover:text-[#103254] dark:hover:text-blue-400 transition-colors bg-gray-50 dark:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ImagePlus className="w-5 h-5" />
            <span className="text-xs">Subir imagen</span>
          </button>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageChange}
          className="hidden"
        />
      </div>

      <div className="flex gap-2 pt-1">
        <Button
          variant="outline"
          onClick={onCancel}
          disabled={isBusy}
          className={`flex-1 ${DLG_BTN_SECONDARY}`}
        >
          Cancelar
        </Button>

        <Button
          onClick={handleSave}
          disabled={!nombre.trim() || isBusy}
          className={`flex-1 ${DLG_BTN_PRIMARY}`}
        >
          {isBusy ? (
            <Loader2 className="w-4 h-4 animate-spin mx-auto" />
          ) : isNew ? (
            'Crear'
          ) : (
            'Guardar'
          )}
        </Button>
      </div>
    </div>
  );
}

// ============================================================
// Componente principal
// ============================================================
export const EditCategoryDialog = ({
  open,
  onClose,
  empresaId,
  mode,
  initialCategory,
  onSaved,
}: Props) => {
  const {
    categorias,
    loading,
    fetchCategorias,
    createCategoria,
    updateCategoria,
    deleteCategoria,
  } = useCategoriasServicio(empresaId);

  const [editingCat, setEditingCat] = useState<CategoriaServicio | null>(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;

    if (mode === 'manage') {
      setEditingCat(null);
      setShowNewForm(false);
      fetchCategorias();
      return;
    }

    if (mode === 'create') {
      setEditingCat(null);
      setShowNewForm(false);
      return;
    }

    if (mode === 'edit') {
      setEditingCat(null);
      setShowNewForm(false);
    }
  }, [open, mode, fetchCategorias]);

  const handleDelete = async (cat: CategoriaServicio) => {
    setDeleting(cat.id_categoria_servicio);

    const resp = await deleteCategoria(cat.id_categoria_servicio);

    if (!resp?.ok) {
      toast.error('Error al eliminar la categoría.');
      setDeleting(null);
      return;
    }

    toast.success(`"${cat.nombre}" eliminada.`);
    onSaved();
    setEditingCat((prev) =>
      prev?.id_categoria_servicio === cat.id_categoria_servicio ? null : prev
    );
    setDeleting(null);
  };

  if (mode === 'create') {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className={SHEET_CLASS}>
          <div className="px-4 sm:px-6 pt-5 pb-3 shrink-0 relative">
            <DialogHeader>
              <DialogTitle className="text-lg sm:text-xl font-semibold text-[#103254] dark:text-blue-400 pr-10">
                Nueva categoría
              </DialogTitle>
            </DialogHeader>
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 sm:px-6 pb-5">
            <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm p-4 text-sm text-gray-700 dark:text-gray-300">
              <CategoryForm
                isNew
                createCategoria={createCategoria}
                updateCategoria={updateCategoria}
                busyExternal={loading}
                onCancel={onClose}
                onSaved={(cat) => {
                  onSaved(cat);
                  onClose();
                }}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (mode === 'edit') {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className={SHEET_CLASS}>
          <div className="px-4 sm:px-6 pt-5 pb-3 shrink-0 relative">
            <DialogHeader>
              <DialogTitle className="text-lg sm:text-xl font-semibold text-[#103254] dark:text-blue-400 pr-10">
                Editar categoría
              </DialogTitle>
            </DialogHeader>
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 sm:px-6 pb-5">
            <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm p-4 text-sm text-gray-700 dark:text-gray-300">
              <CategoryForm
                key={initialCategory?.id_categoria_servicio}
                initial={initialCategory}
                isNew={false}
                createCategoria={createCategoria}
                updateCategoria={updateCategoria}
                busyExternal={loading}
                onCancel={onClose}
                onSaved={(cat) => {
                  onSaved(cat);
                  onClose();
                }}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className={SHEET_CLASS}>
        <div className="px-4 sm:px-6 pt-5 pb-3 shrink-0 relative">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-semibold text-[#103254] dark:text-blue-400 pr-10">
              Categorías de servicios
            </DialogTitle>
          </DialogHeader>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 sm:px-6 pb-5">
          <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm p-4 space-y-4 text-sm text-gray-700 dark:text-gray-300">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-400" />
              </div>
            ) : (
              <div className="space-y-2">
                {categorias.length === 0 && !showNewForm && (
                  <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-4">
                    Todavía no hay categorías
                  </p>
                )}

                {categorias.map((catAny) => {
                  const cat: CategoriaServicio = {
                    id_categoria_servicio: catAny.id_categoria_servicio,
                    nombre: catAny.nombre,
                    imagen: catAny.imagen ?? null,
                    icono: catAny.icono ?? null,
                    orden: catAny.orden ?? null,
                    activo: catAny.activo ?? true,
                  };

                  return (
                    <div key={cat.id_categoria_servicio}>
                      {editingCat?.id_categoria_servicio !==
                      cat.id_categoria_servicio ? (
                        <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5">
                          {cat.imagen ? (
                            <img
                              src={cat.imagen}
                              alt={cat.nombre}
                              className="w-9 h-9 rounded-md object-cover shrink-0 border border-gray-200 dark:border-white/10"
                            />
                          ) : (
                            <div className="w-9 h-9 rounded-md bg-gray-200 dark:bg-white/10 flex items-center justify-center shrink-0">
                              <ImagePlus className="w-4 h-4 text-gray-400" />
                            </div>
                          )}

                          <span className="flex-1 text-sm font-medium text-gray-800 dark:text-white truncate">
                            {cat.nombre}
                          </span>

                          <div className="flex items-center gap-1 shrink-0">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setEditingCat(cat);
                                setShowNewForm(false);
                              }}
                              className="h-7 w-7 text-gray-500 hover:text-[#103254] dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-white/10"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>

                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(cat)}
                              disabled={deleting === cat.id_categoria_servicio}
                              className="h-7 w-7 text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                            >
                              {deleting === cat.id_categoria_servicio ? (
                                <div className="w-3.5 h-3.5 animate-spin rounded-full border-t border-gray-400" />
                              ) : (
                                <Trash2 className="w-3.5 h-3.5" />
                              )}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="rounded-xl border border-[#103254]/20 dark:border-blue-500/30 bg-white dark:bg-white/5 p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-semibold text-[#103254] dark:text-blue-400">
                              Editar categoría
                            </h3>
                            <button
                              type="button"
                              onClick={() => setEditingCat(null)}
                              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>

                          <CategoryForm
                            key={cat.id_categoria_servicio}
                            initial={cat}
                            isNew={false}
                            createCategoria={createCategoria}
                            updateCategoria={updateCategoria}
                            busyExternal={loading}
                            onCancel={() => setEditingCat(null)}
                            onSaved={() => {
                              onSaved();
                              fetchCategorias();
                              setEditingCat(null);
                            }}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {showNewForm ? (
              <div className="rounded-xl border border-[#103254]/20 dark:border-blue-500/30 bg-white dark:bg-white/5 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-[#103254] dark:text-blue-400">
                    Nueva categoría
                  </h3>
                  <button
                    type="button"
                    onClick={() => setShowNewForm(false)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <CategoryForm
                  isNew
                  createCategoria={createCategoria}
                  updateCategoria={updateCategoria}
                  busyExternal={loading}
                  onCancel={() => setShowNewForm(false)}
                  onSaved={() => {
                    onSaved();
                    fetchCategorias();
                    setShowNewForm(false);
                  }}
                />
              </div>
            ) : (
              <Button
                onClick={() => {
                  setShowNewForm(true);
                  setEditingCat(null);
                }}
                variant="outline"
                className="w-full border-dashed border-gray-300 dark:border-white/10 text-gray-600 dark:text-gray-400 hover:border-[#103254]/50 dark:hover:border-blue-500/50 hover:text-[#103254] dark:hover:text-blue-400 transition-colors"
              >
                + Nueva categoría
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};